import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, ChevronLeft, ChevronRight, Video, MapPin, Phone } from 'lucide-react';
import type { MeetingType } from '@pullman/shared';
import { useTranslation } from '@/i18n';

import type { TranslationKey } from '@pullman/shared';

const MEETING_TYPES: { type: MeetingType; icon: typeof Video; labelKey: TranslationKey }[] = [
  { type: 'in_person', icon: MapPin, labelKey: 'meeting.inPerson' },
  { type: 'video_call', icon: Video, labelKey: 'meeting.videoCall' },
  { type: 'phone_call', icon: Phone, labelKey: 'meeting.phoneCall' },
];

const TIME_SLOTS = ['09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfWeek(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const DAY_NAMES = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export function MeetingScheduler() {
  const { t } = useTranslation();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [meetingType, setMeetingType] = useState<MeetingType>('video_call');
  const [confirmed, setConfirmed] = useState(false);

  const daysInMonth = useMemo(() => getDaysInMonth(year, month), [year, month]);
  const firstDay = useMemo(() => getFirstDayOfWeek(year, month), [year, month]);
  const today = now.getDate();
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth();

  const prevMonth = () => {
    if (month === 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
    setSelectedDate(null);
  };

  const nextMonth = () => {
    if (month === 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
    setSelectedDate(null);
  };

  const handleConfirm = () => {
    setConfirmed(true);
  };

  if (confirmed) {
    return (
      <motion.div
        className="bg-white rounded-card shadow-lg border border-gray-100 p-6 text-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
          <Calendar size={20} className="text-green-600" />
        </div>
        <h3 className="font-display text-base text-brand-charcoal mb-1">{t('meeting.requested')}</h3>
        <p className="text-sm text-brand-charcoal/60">
          {MONTH_NAMES[month]} {selectedDate}, {year} at {selectedTime}
        </p>
        <p className="text-sm text-brand-charcoal/60 mt-1">
          {t(MEETING_TYPES.find((m) => m.type === meetingType)?.labelKey ?? 'meeting.videoCall')}
        </p>
        <p className="text-xs text-brand-charcoal/40 mt-4">
          {t('meeting.confirmNote')}
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="bg-white rounded-card shadow-lg overflow-hidden border border-gray-100"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className="bg-brand-navy p-4">
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-brand-gold" />
          <h3 className="font-display text-sm tracking-wider text-white uppercase">
            {t('meeting.title')}
          </h3>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Meeting type */}
        <div className="flex gap-2">
          {MEETING_TYPES.map((mt) => {
            const Icon = mt.icon;
            return (
              <button
                key={mt.type}
                onClick={() => setMeetingType(mt.type)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs rounded-lg transition-all ${
                  meetingType === mt.type
                    ? 'bg-brand-navy text-white'
                    : 'border border-gray-200 text-brand-charcoal/70 hover:border-brand-gold/50'
                }`}
              >
                <Icon size={14} />
                {t(mt.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Calendar header */}
        <div className="flex items-center justify-between">
          <button onClick={prevMonth} className="p-1 text-brand-charcoal/40 hover:text-brand-charcoal" aria-label="Previous month">
            <ChevronLeft size={18} />
          </button>
          <p className="text-sm font-semibold text-brand-charcoal">
            {MONTH_NAMES[month]} {year}
          </p>
          <button onClick={nextMonth} className="p-1 text-brand-charcoal/40 hover:text-brand-charcoal" aria-label="Next month">
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Calendar grid */}
        <div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_NAMES.map((d) => (
              <div key={d} className="text-center text-[11px] text-brand-charcoal/40 font-medium py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const isPast = isCurrentMonth && day < today;
              const isSunday = new Date(year, month, day).getDay() === 0;
              const isDisabled = isPast || isSunday;
              const isSelected = selectedDate === day;

              return (
                <button
                  key={day}
                  onClick={() => !isDisabled && setSelectedDate(day)}
                  disabled={isDisabled}
                  className={`h-8 text-xs rounded-lg transition-all ${
                    isSelected
                      ? 'bg-brand-navy text-white font-semibold'
                      : isDisabled
                        ? 'text-brand-charcoal/20 cursor-not-allowed'
                        : 'text-brand-charcoal hover:bg-brand-cream'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time slots */}
        {selectedDate && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-charcoal/50 mb-2 flex items-center gap-1.5">
              <Clock size={12} />
              {t('calendar.selectTime')}
            </p>
            <div className="grid grid-cols-4 gap-2">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setSelectedTime(slot)}
                  className={`py-1.5 text-xs rounded-lg transition-all ${
                    selectedTime === slot
                      ? 'bg-brand-navy text-white font-semibold'
                      : 'border border-gray-200 text-brand-charcoal/70 hover:border-brand-gold/50'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Confirm / Cancel */}
        <div className="flex gap-2 pt-2">
          <button
            onClick={handleConfirm}
            disabled={!selectedDate || !selectedTime}
            className="flex-1 py-2.5 text-sm rounded-full bg-brand-navy text-white
              hover:bg-brand-charcoal disabled:opacity-30 transition-all"
          >
            {t('calendar.confirm')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
